import { FunctionComponent, useCallback, useEffect, useRef } from 'react'
import { reactular } from '@vendhq/reactular'
import Shepherd from 'tether-shepherd'
import { trackUserEvent } from '@vend/utilities'
import { IPromise } from 'angular'
import {
  usePosStateManager,
  useSaleManager,
  useTrainingModeManager,
} from '../../ng-compat/hooks'
import { WEB_REGISTER } from '../web-register.constant'
import { $q, $rootScope, $state } from '../../ng-compat/imports'
import { STATES } from '../../../../lib/routes'
import { SaleManagerEvents } from '../../managers/sale-manager'
import { EVENT } from '../../constants'
import { useSteps } from './useSteps'

interface TourStepConfig {
  id: string
  title: string
  text: string
  attachTo: string | { element: string; on: 'bottom' }
  tetherOptions?: unknown
  buttons?: Array<{
    text: string
    action: string
  }>
  unbind?: Array<() => void>
}

const saleManagerEvents = [EVENT.SALE_ADD_LINE_ITEM]

const isSaleManagerEvent = (
  event: string
): event is keyof SaleManagerEvents => {
  return (saleManagerEvents as string[]).includes(event)
}

export const TrainingTour: FunctionComponent = () => {
  const trainingModeManager = useTrainingModeManager()
  const posStateManager = usePosStateManager()
  const saleManager = useSaleManager()
  const tourRef = useRef<Shepherd.Tour | null>(null)

  const getTourSteps = useSteps()

  const initiateTour = useCallback(async () => {
    const tour = new Shepherd.Tour({
      defaults: {
        classes: 'wr-tour',
        buttons: [],
        showCancelLink: false,
      },
    })
    tourRef.current = tour
    // Need to run this for side effects but actually returns
    // untranslated steps, so discard them
    await trainingModeManager.getTourSteps()

    const tourType = posStateManager.getTourType()
    if (!tourType) {
      return
    }
    const tourSteps = getTourSteps(tourType)
    const numberOfSteps = tourSteps.length

    tourSteps.forEach((stepDetails, index) => {
      const stepNumber = index + 1
      const stepConfig: TourStepConfig = {
        id: stepDetails.id,
        title: stepDetails.pagination
          ? stepDetails.title
              .concat(`<span class="wr-tour-title-pagination vd-text-supplementary">
              ${stepNumber}/${numberOfSteps}</span>`)
          : stepDetails.title,
        text: stepDetails.text,
        attachTo: stepDetails.attachTo,
        tetherOptions: stepDetails.tetherOptions,
      }
      if (stepDetails.nextBtn) {
        stepConfig.buttons = [
          {
            text: 'Okay, thanks!',
            action: tour.next,
          },
        ]
      }

      tour.addStep(stepConfig)

      const currentStep = tour.getById(stepConfig.id)
      if (!currentStep) {
        return
      }
      const stepIndex = tour.steps.indexOf(currentStep)
      const step = tour.steps[stepIndex]

      const bindStepTrigger = (options: {
        stepId: string
        triggerEvent: string
        stepDelay?: number
        trackingData?: { [key: string]: { step: string } }
        isSaleManagerEvent?: boolean
      }) => {
        const handler = () => {
          const stepDelay = 200
          const trackingData =
            options.trackingData && options.trackingData[options.triggerEvent]

          // Track the step if there is data available for the specific event
          if (trackingData) {
            trackUserEvent(
              WEB_REGISTER.APPLICATION_NAME,
              trainingModeManager.currentTourType || '',
              trackingData
            )
          }

          setTimeout(() => {
            // Handles cases where tour has not been destroyed quite yet when a trigger event happens
            if (tour) {
              tour.show(options.stepId)
            }
          }, stepDelay)
        }

        if (isSaleManagerEvent(options.triggerEvent)) {
          const triggerEvent = options.triggerEvent
          saleManager.on(triggerEvent, handler)
          return () => {
            saleManager.off(triggerEvent, handler)
          }
        }
        return $rootScope.$on(options.triggerEvent, handler)
      }

      if (stepDetails.triggerOn) {
        // Sometimes more than one trigger event is configured
        if (Array.isArray(stepDetails.triggerOn)) {
          // @ts-expect-error
          step.unbind = []
          stepDetails.triggerOn.forEach((trigger: $TSFixMe) => {
            const unbind = bindStepTrigger({
              stepId: stepConfig.id,
              triggerEvent: trigger,
              stepDelay: stepDetails.delay,
              trackingData: stepDetails.trackingData,
            })

            // @ts-expect-error
            step.unbind.push(unbind)
          })
        } else {
          // Ensure the unbind method is saved to the actual step
          // @ts-expect-error
          step.unbind = bindStepTrigger({
            stepId: stepConfig.id,
            triggerEvent: stepDetails.triggerOn,
            stepDelay: stepDetails.delay,
            trackingData: stepDetails.trackingData,
          })
        }
      }
    })

    const transitionToSaleRoute = !$state.includes(STATES.SALE)
      ? (($state.go(STATES.SALE) as unknown) as IPromise<void>)
      : $q.resolve()

    // Support initiating the tour from step 1 from anywhere
    transitionToSaleRoute.then(() => {
      $rootScope.$applyAsync(() => tour.start())
    })
  }, [getTourSteps, posStateManager, saleManager, trainingModeManager])

  const destroyTour = useCallback(() => {
    const tour = tourRef.current
    if (!tour) {
      return
    }

    tour.steps.forEach(step => {
      // @ts-expect-error
      const unbind: (() => void) | undefined = step.unbind
      if (unbind) {
        if (Array.isArray(unbind)) {
          unbind.forEach(fn => fn())
        } else {
          unbind()
        }
      }
      // @ts-expect-error
      if (step.destroy) {
        // @ts-expect-error
        step.destroy()
      }
    })
    tourRef.current = null
  }, [])

  useEffect(() => {
    const handleTourChange = () => {
      if (trainingModeManager.tourEnabled) {
        initiateTour()
      } else {
        destroyTour()
      }
    }

    trainingModeManager.on('tourEnabledChange', handleTourChange)
    return () => {
      trainingModeManager.off('tourEnabledChange', handleTourChange)
    }
  }, [destroyTour, initiateTour, trainingModeManager])

  return null
}

export const ReactTrainingTour = reactular(TrainingTour, [], 'reactWrapper')
